import React, { useCallback, useEffect, useState } from "react";
import { AdminClient } from "../../../api/generated/APIClient";
import type { AdminRole } from "../../../api/adminAuth";
import { useAuth } from "../../../contexts/AuthContext";
import "./AdminUserPage.css";

// 開発者（管理ユーザー）の管理。サーバー管理者だけが使える（サーバーでも確認している）

interface AdminUser {
  id: string;
  mail: string;
  admin_name: string;
  role: AdminRole;
  role_label: string;
  is_first_login: boolean;
  last_login_date?: string;
  create_date?: string;
}

interface RoleOption {
  value: AdminRole;
  label: string;
}

interface DraftUser {
  id?: string;
  mail: string;
  adminName: string;
  password: string;
  role: AdminRole;
}

const emptyDraft = (): DraftUser => ({
  mail: "",
  adminName: "",
  password: "",
  role: "admin",
});

const formatDate = (s: string | undefined): string => {
  if (!s) return "-";
  const d = new Date(s);
  // Go のゼロ値（0001-01-01）は「未ログイン」として扱う
  if (isNaN(d.getTime()) || d.getFullYear() < 2000) return "-";
  return d.toLocaleString("ja-JP");
};

// 初期パスワードの自動生成（紛らわしい文字 0/O・1/l/I は除く）。サーバーの基準（12 文字以上）より長めにする
const generatePassword = (): string => {
  const chars = "abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789-_!#%+";
  const limit = 256 - (256 % chars.length); // 偏りが出ないよう、端数の乱数は捨てる
  let out = "";
  while (out.length < 16) {
    const buf = new Uint8Array(32);
    crypto.getRandomValues(buf);
    for (const b of buf) {
      if (b < limit && out.length < 16) out += chars[b % chars.length];
    }
  }
  return out;
};

const readError = async (res: Response, fallback: string): Promise<string> =>
  (await res.json().catch(() => ({})))?.error || `${fallback} (${res.status})`;

export const AdminUserPage: React.FC = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [roleOptions, setRoleOptions] = useState<RoleOption[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<DraftUser | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const isServerAdmin = me?.role === "server_admin";

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/admin/admin-users", { credentials: "same-origin" });
      if (!res.ok) throw new Error(await readError(res, "管理ユーザーの取得に失敗"));
      const data = await res.json();
      setUsers(data.admin_users ?? []);
      setRoleOptions(data.roles ?? []);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "管理ユーザーの取得に失敗しました");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isServerAdmin) void fetchUsers();
  }, [fetchUsers, isServerAdmin]);

  if (!isServerAdmin) {
    return (
      <div className="admin-user-page">
        <div className="admin-user-header">
          <h1>管理ユーザー</h1>
        </div>
        <div className="admin-user-error">この画面はサーバー管理者だけが利用できます。</div>
      </div>
    );
  }

  const startNew = () => {
    setIsNew(true);
    setEditing(emptyDraft());
    setShowPassword(false);
    setErr(null);
  };

  const startEdit = (u: AdminUser) => {
    setIsNew(false);
    setEditing({ id: u.id, mail: u.mail, adminName: u.admin_name, password: "", role: u.role });
    setShowPassword(false);
    setErr(null);
  };

  const cancel = () => {
    setEditing(null);
    setErr(null);
  };

  const save = async () => {
    if (!editing) return;
    setBusy(true);
    setErr(null);
    try {
      const res = isNew
        ? await fetch("/api/admin/admin-users", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              mail: editing.mail,
              admin_name: editing.adminName,
              password: editing.password,
              role: editing.role,
            }),
          })
        : await fetch(`/api/admin/admin-users/${encodeURIComponent(editing.id ?? "")}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              admin_name: editing.adminName,
              new_password: editing.password,
              role: editing.role,
            }),
          });
      if (!res.ok) throw new Error(await readError(res, isNew ? "作成に失敗" : "更新に失敗"));
      setEditing(null);
      await fetchUsers();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "保存に失敗しました");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (u: AdminUser) => {
    if (!window.confirm(`管理ユーザー「${u.admin_name || u.mail}」を削除しますか？`)) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await (AdminClient as any).DeleteAdminUser({ ids: [u.id], force_execute: true });
      if (res?.error) throw new Error(res.error);
      await fetchUsers();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "削除に失敗しました");
    } finally {
      setBusy(false);
    }
  };

  const editingSelf = !isNew && editing?.id === me?.id;

  return (
    <div className="admin-user-page">
      <div className="admin-user-header">
        <h1>管理ユーザー</h1>
        <button className="btn btn-primary" onClick={startNew} disabled={busy}>
          + 新規追加
        </button>
      </div>

      <p className="hint" style={{ margin: "0 0 12px", color: "#6b7280", fontSize: 13 }}>
        サーバー管理者は開発者アカウントの管理を含むすべての操作、管理者はサイトの運用（お問い合わせ・お知らせ・施工実績・メンテナンス・公開）ができます。
      </p>

      {err && <div className="admin-user-error">{err}</div>}

      <div className="admin-user-table-wrap">
        <table className="admin-user-table">
          <thead>
            <tr>
              <th>メールアドレス</th>
              <th>表示名</th>
              <th>ロール</th>
              <th>最終ログイン</th>
              <th>作成日</th>
              <th className="col-actions">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading && users.length === 0 ? (
              <tr><td colSpan={6} className="empty">読み込み中...</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={6} className="empty">管理ユーザーがいません</td></tr>
            ) : (
              users.map((u) => (
                <tr key={u.id} data-role={u.role}>
                  <td>{u.mail}</td>
                  <td>
                    {u.admin_name}
                    {u.is_first_login && (
                      <span style={{ marginLeft: 6, fontSize: 11, color: "#b45309" }}>（パスワード変更待ち）</span>
                    )}
                  </td>
                  <td className="col-role">{u.role_label}</td>
                  <td>{formatDate(u.last_login_date)}</td>
                  <td>{formatDate(u.create_date)}</td>
                  <td className="col-actions">
                    <button className="btn btn-sm" onClick={() => startEdit(u)} disabled={busy}>編集</button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => remove(u)}
                      disabled={busy || u.id === me?.id}
                      title={u.id === me?.id ? "自分自身は削除できません" : undefined}
                    >
                      削除
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <>
          <div className="admin-user-modal-backdrop" onClick={cancel} />
          <div className="admin-user-modal" role="dialog" aria-modal="true">
            <h2>{isNew ? "管理ユーザーを追加" : "管理ユーザーを編集"}</h2>

            <div className="form-row">
              <label>メールアドレス</label>
              <input
                type="email"
                value={editing.mail}
                disabled={!isNew}
                onChange={(e) => setEditing({ ...editing, mail: e.target.value })}
                placeholder="admin@example.com"
              />
              {!isNew && <p className="hint">メールアドレスは ID の元になるため変更不可です</p>}
            </div>

            <div className="form-row">
              <label>表示名</label>
              <input
                type="text"
                value={editing.adminName}
                onChange={(e) => setEditing({ ...editing, adminName: e.target.value })}
                placeholder="管理者"
              />
            </div>

            <div className="form-row">
              <label htmlFor="admin-user-role">ロール</label>
              <select
                id="admin-user-role"
                value={editing.role}
                disabled={editingSelf}
                onChange={(e) => setEditing({ ...editing, role: e.target.value as AdminRole })}
                style={{ padding: "8px 10px", borderRadius: 6, border: "1px solid #d1d5db" }}
              >
                {roleOptions.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
              {editingSelf && <p className="hint">自分のロールは変更できません</p>}
            </div>

            {editingSelf ? (
              <p className="hint">自分のパスワードは画面右上の「パスワード変更」から変更してください。</p>
            ) : (
              <div className="form-row">
                <label>
                  {isNew ? "初期パスワード" : "パスワードの再設定"}{" "}
                  {isNew ? <span className="required">*</span> : <span className="hint">（空欄で据え置き）</span>}
                </label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={editing.password}
                    autoComplete="new-password"
                    onChange={(e) => setEditing({ ...editing, password: e.target.value })}
                    placeholder="12 文字以上"
                    style={{ flex: 1, fontFamily: showPassword ? "monospace" : undefined }}
                  />
                  <button type="button" className="btn btn-sm" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? "隠す" : "表示"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => {
                      setEditing({ ...editing, password: generatePassword() });
                      setShowPassword(true);
                    }}
                  >
                    自動生成
                  </button>
                </div>
                <p className="hint">
                  本人に安全な方法で伝えてください。本人は次回ログイン時に自分でパスワードを決め直します
                  （再設定すると、本人がログイン中の端末はすべてログアウトされます）。
                </p>
              </div>
            )}

            <div className="form-actions">
              <button className="btn" onClick={cancel} disabled={busy}>キャンセル</button>
              <button className="btn btn-primary" onClick={save} disabled={busy}>
                {busy ? "保存中..." : isNew ? "作成" : "更新"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminUserPage;
